import { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';
import type { Contact, PaginatedResponse } from '../types';

interface UseContactsParams {
  page?: number;
  pageSize?: number;
  search?: string;
  source?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

interface UseContactsResult {
  contacts: Contact[];
  total: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useContacts(params: UseContactsParams = {}): UseContactsResult {
  const {
    page = 1,
    pageSize = 20,
    search = '',
    source = '',
    sortBy = '',
    sortOrder = 'desc',
  } = params;

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();
      queryParams.set('page', String(page));
      queryParams.set('pageSize', String(pageSize));
      if (search) queryParams.set('search', search);
      if (source) queryParams.set('source', source);
      if (sortBy) queryParams.set('sortBy', sortBy);
      if (sortOrder) queryParams.set('sortOrder', sortOrder);

      const result = await api.get<PaginatedResponse<Contact>>(
        `/api/contacts?${queryParams.toString()}`
      );

      setContacts(result.data);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载联系人失败');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, source, sortBy, sortOrder]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  return { contacts, total, loading, error, refetch: fetchContacts };
}
